import {
  InlineEditItem,
  InlineEditResolution,
  ScannerError,
} from '@amzn/awscat-pattern-library-service-model';
import PQueue from 'p-queue';

import { IMplServiceClient } from '../api/IMplServiceClient';
import { log } from '../loggers/Logger';
import { ServiceCallError } from './Errors';
import { TypedEmitter, Unsubscribe } from './TypedEmitter';

/** Events emitted by {@link InlineEditsManager}. */
export type InlineEditsManagerEvents = {
  /** Fired after `openedEdits` changes (open, close, refresh, or any mutation). */
  changed: () => void;
};

/** Snapshot of scanner-suggested inline edits for the currently-open content. */
export interface OpenedEdits {
  readonly contentId: string;
  readonly edits: readonly InlineEditItem[];
  readonly errors: readonly ScannerError[];
  readonly unresolvedCount: number;
  readonly totalCount: number;
}

/**
 * Owns inline-edit state for one opened content.
 *
 * Only one content open at a time,
 * mutations are serialized via PQueue, and `changed` is emitted after
 * every state change so consumers can re-read `openedEdits`.
 */
export class InlineEditsManager {
  private readonly _mpl: IMplServiceClient;
  private readonly _emitter = new TypedEmitter<InlineEditsManagerEvents>();
  private readonly _queue = new PQueue({ concurrency: 1 });

  private _opened: OpenedEdits | undefined = undefined;

  constructor(mpl: IMplServiceClient) {
    this._mpl = mpl;
  }

  /** Currently-open edits snapshot, or undefined if nothing is open. */
  get openedEdits(): OpenedEdits | undefined {
    return this._opened;
  }

  /**
   * Fetch inline edits for `contentId` and populate `openedEdits`.
   *
   * @throws {ServiceCallError} when the backend call fails
   */
  async open(contentId: string): Promise<void> {
    await this._queue.add(() => this.doOpen(contentId));
  }

  /** Drop the open snapshot. Safe to call when nothing is open. */
  async close(): Promise<void> {
    await this._queue.add(() => this.doClose());
  }

  /**
   * Re-fetch the edit list from the server. No-ops when nothing is open.
   *
   * @throws {ServiceCallError} when the backend call fails
   */
  async refresh(): Promise<void> {
    await this._queue.add(() => this.refreshInternal());
  }

  /**
   * Accept or reject a single edit, then refresh.
   *
   * @throws {ServiceCallError} when the backend call fails
   */
  async resolveEdit(editId: string, resolution: InlineEditResolution): Promise<void> {
    await this._queue.add(() => this.doResolveEdit(editId, resolution));
  }

  /**
   * Resolves an edit if not already resolved; logs a warning and skips otherwise.
   *
   * @throws {ServiceCallError} when the backend call fails
   */
  async tryResolveEdit(editId: string, resolution: InlineEditResolution): Promise<void> {
    await this._queue.add(async () => {
      const edit = this._opened?.edits.find((e) => e.editId === editId);
      if (edit?.resolved) {
        log.warn(`InlineEditsManager: edit "${editId}" is already resolved, skipping.`);
        return;
      }
      await this.doResolveEdit(editId, resolution);
    });
  }

  /**
   * Accept all unresolved edits in one batch, then refresh.
   *
   * @throws {ServiceCallError} when the backend call fails
   */
  async bulkResolve(): Promise<void> {
    await this._queue.add(() => this.doBulkResolve());
  }

  /** Subscribe to an event. Returns an unsubscribe function. */
  on<K extends keyof InlineEditsManagerEvents>(
    event: K,
    listener: InlineEditsManagerEvents[K],
  ): Unsubscribe {
    return this._emitter.on(event, listener);
  }

  private async doOpen(contentId: string): Promise<void> {
    const response = await this._mpl
      .getInlineEdits(contentId)
      .catch((err) => InlineEditsManager.ignoreValidationError(err, contentId));
    this._opened = {
      contentId,
      edits: response?.edits ?? [],
      errors: response?.errors ?? [],
      unresolvedCount: response?.unresolvedCount ?? 0,
      totalCount: response?.totalCount ?? 0,
    };
    this._emitter.emit('changed');
  }

  private doClose(): void {
    this._opened = undefined;
    this._emitter.emit('changed');
  }

  /**
   * Refresh without re-queuing. Used at the tail of queued mutations so the
   * final state is visible when the caller's `await` resolves. Enqueuing
   * here would deadlock on the single-concurrency queue.
   */
  private async refreshInternal(): Promise<void> {
    const current = this._opened;
    if (!current) return;

    const response = await this._mpl
      .getInlineEdits(current.contentId)
      .catch((err) => InlineEditsManager.ignoreValidationError(err, current.contentId));
    this._opened = {
      contentId: current.contentId,
      edits: response?.edits ?? [],
      errors: response?.errors ?? [],
      unresolvedCount: response?.unresolvedCount ?? 0,
      totalCount: response?.totalCount ?? 0,
    };
    this._emitter.emit('changed');
  }

  /** @throws {ServiceCallError} when the backend call fails (optimistic update is reverted first). */
  private async doResolveEdit(editId: string, resolution: InlineEditResolution): Promise<void> {
    if (!this._opened) return;

    // Snapshot state for rollback on failure
    const previousEdits = this._opened.edits;
    const previousCount = this._opened.unresolvedCount;

    // Update the resolved edit in memory first
    const updatedEdits = this._opened.edits.map((edit) =>
      edit.editId === editId ? { ...edit, resolved: true, resolution } : edit,
    );
    this._opened = {
      ...this._opened,
      edits: updatedEdits,
      unresolvedCount: updatedEdits.filter((e) => !e.resolved).length,
    };
    this._emitter.emit('changed');

    // Then flush the change to backend
    try {
      await this._mpl.resolveInlineEdit(this._opened.contentId, editId, resolution);
    } catch (e) {
      // Revert optimistic update on failure
      if (this._opened) {
        this._opened = { ...this._opened, edits: previousEdits, unresolvedCount: previousCount };
        this._emitter.emit('changed');
      }
      throw e;
    }
  }

  /** @throws {ServiceCallError} when the backend call fails (optimistic update is reverted first). */
  private async doBulkResolve(): Promise<void> {
    if (!this._opened) return;

    // Snapshot state for rollback on failure
    const previousEdits = this._opened.edits;
    const previousCount = this._opened.unresolvedCount;

    // Mark all unresolved edits as resolved in memory first
    const updatedEdits = this._opened.edits.map((edit) =>
      edit.resolved ? edit : { ...edit, resolved: true, resolution: InlineEditResolution.ACCEPTED },
    );
    this._opened = {
      ...this._opened,
      edits: updatedEdits,
      unresolvedCount: 0,
    };
    this._emitter.emit('changed');

    // Then flush to backend
    try {
      await this._mpl.bulkResolveInlineEdits(this._opened.contentId);
    } catch (e) {
      // Revert optimistic update on failure
      if (this._opened) {
        this._opened = { ...this._opened, edits: previousEdits, unresolvedCount: previousCount };
        this._emitter.emit('changed');
      }
      throw e;
    }
  }

  /**
   * Swallows a validation error from `getInlineEdits` so the caller can treat
   * it as an empty response; rethrows anything else. The server returns a
   * validation error when inline edits do not apply to the content's current
   * state.
   */
  private static ignoreValidationError(err: unknown, contentId: string): undefined {
    if (err instanceof ServiceCallError && err.isValidationError) {
      log.warn({ err, contentId }, 'Server indicated that inline edits are unavailable');
      return undefined;
    }
    throw err;
  }
}
